import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { productsApi, categoriesApi, sellersApi, uploadApi } from '../../services/apiService';
import { getProductLimitInfo, canUploadProduct, getRemainingImageSlots } from '../../utils/membershipGates';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import SellerDashboardLayout from '../../components/SellerDashboardLayout/SellerDashboardLayout';
import { API_URL } from '../../config/api';
import './ProductInputPage.css';

const ProductInputPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editProductId = searchParams.get('edit');
  const isAdminEdit = searchParams.get('admin') === 'true';
  const { user } = useAuth();
  const { getFeature, planDisplayName, planFeatures, loading: subscriptionLoading } = useSubscription();
  const editorRef = useRef(null);
  const imageInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  
  const [activeMenu, setActiveMenu] = useState('products');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSubcategoryDropdown, setShowSubcategoryDropdown] = useState(false);
  const [showSellerDropdown, setShowSellerDropdown] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [productCount, setProductCount] = useState(0);
  const [imageSlots, setImageSlots] = useState(5); // Start with 5 slots, can add more
  const [categoryError, setCategoryError] = useState(false);
  
  // Get plan limits - wait for subscription to load
  // Admin bypass - admin has unlimited access
  const isAdminUser = user?.adminRole === 'admin' || user?.adminRole === 'subadmin';
  const productLimit = isAdminUser ? 999999 : (getFeature('productPostingLimit') || 10);
  const maxImages = isAdminUser ? 999 : (getFeature('maxProductImages') || 5);
  
  // Debug logging
  console.log('[ProductInput] planFeatures:', planFeatures);
  console.log('[ProductInput] productLimit:', productLimit, '| maxImages:', maxImages);
  
  // Get product limit info using the utility
  const limitInfo = getProductLimitInfo(productCount, planFeatures);
  
  const [formData, setFormData] = useState({
    name: '',
    tagline: '',
    description: '',
    priceMin: '',
    priceMax: '',
    unit: 'Units',
    moq: '',
    categoryId: '',
    subcategoryId: '',
    sellerId: '',
    image: '',
    images: [],
    document: null,
    documentName: '',
    status: 'pending',
    isNew: true,
    isFeatured: false,
    isPopular: false
  });

  // Fetch categories and sellers on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, sellersRes] = await Promise.all([
          categoriesApi.getAll(),
          sellersApi.getAll()
        ]);
        setCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
        setSellers(Array.isArray(sellersRes) ? sellersRes : []);
        
        // Fetch user's current product count for limit check
        if (user?.id) {
          try {
            const userProducts = await productsApi.getAll({ userId: user.id });
            setProductCount(Array.isArray(userProducts) ? userProducts.length : 0);
          } catch (err) {
            console.error('Error fetching user product count:', err);
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };
    fetchData();
  }, [user?.id]);

  // Update subcategories when category changes
  useEffect(() => {
    if (formData.categoryId) {
      const selectedCategory = categories.find(c => c.id === parseInt(formData.categoryId));
      if (selectedCategory && selectedCategory.subcategories) {
        setSubcategories(selectedCategory.subcategories);
      } else {
        setSubcategories([]);
      }
    } else {
      setSubcategories([]);
    }
  }, [formData.categoryId, categories]);

  // Load product data if editing
  useEffect(() => {
    const loadProduct = async () => {
      if (editProductId) {
        try {
          setLoading(true);
          const product = await productsApi.getById(editProductId);
          
          // Parse images - handle both string and array formats
          let parsedImages = [];
          if (product.images) {
            try {
              parsedImages = typeof product.images === 'string' 
                ? JSON.parse(product.images) 
                : product.images;
            } catch (e) {
              console.error('Error parsing product images:', e);
              parsedImages = [];
            }
          }
          
          // Filter out empty/null values
          parsedImages = parsedImages.filter(Boolean);
          
          // Set image slots based on existing images (minimum 5)
          if (parsedImages.length > 0) {
            setImageSlots(Math.max(5, parsedImages.length));
          }
          
          setFormData({
            name: product.name || '',
            tagline: product.tagline || '',
            description: product.description || '',
            priceMin: product.priceMin || '',
            priceMax: product.priceMax || '',
            unit: product.unit || 'Tons',
            moq: product.moq || '',
            categoryId: product.categoryId || '',
            subcategoryId: product.subcategoryId || '',
            sellerId: product.sellerId || '',
            image: product.image || '',
            images: parsedImages,
            status: product.status || 'pending',
            isNew: product.isNew !== undefined ? product.isNew : true,
            isFeatured: product.isFeatured || false,
            isPopular: product.isPopular || false
          });
          
          console.log('[ProductInput] Loaded product with images:', parsedImages);
        } catch (err) {
          console.error('Error loading product:', err);
          setError('Failed to load product');
        } finally {
          setLoading(false);
        }
      }
    };
    loadProduct();
  }, [editProductId]);

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    setFormData(prev => ({
      ...prev,
      categoryId: categoryId,
      subcategoryId: '' // Reset subcategory when category changes
    }));
    setShowCategoryDropdown(false);
    setCategoryError(false); // Clear error when category is selected
  };

  // Handle subcategory selection
  const handleSubcategorySelect = (subcategoryId) => {
    setFormData(prev => ({
      ...prev,
      subcategoryId: subcategoryId
    }));
    setShowSubcategoryDropdown(false);
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle seller selection
  const handleSellerSelect = (sellerId) => {
    setFormData(prev => ({
      ...prev,
      sellerId: sellerId
    }));
    setShowSellerDropdown(false);
  };

  // Handle image file upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // Check if adding these images would exceed the limit
    const currentCount = formData.images.length;
    const remainingSlots = maxImages - currentCount;
    
    if (files.length > remainingSlots) {
      setError(`You can only upload ${remainingSlots} more image(s). Maximum ${maxImages} images allowed.`);
      return;
    }

    setUploadingImage(true);
    setError(null);

    try {
      const uploadPromises = files.map(file => uploadApi.uploadImage(file, 'images'));
      const results = await Promise.all(uploadPromises);
      
      const newImageUrls = results.map(result => {
        // Construct full URL if it's a relative path
        const imageUrl = result.imageUrl || result.url;
        // Use base URL (without /api) for uploads
        const baseUrl = API_URL.replace('/api', '');
        return imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl}`;
      });

      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...newImageUrls],
        image: prev.image || newImageUrls[0]
      }));
    } catch (err) {
      console.error('Error uploading images:', err);
      setError(err.response?.data?.error || 'Failed to upload images. Please try again.');
    } finally {
      setUploadingImage(false);
      // Reset input so same file can be selected again
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  // Handle document file upload
  const handleDocumentUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size exceeds 50MB limit.');
      return;
    }

    setUploadingDocument(true);
    setError(null);

    try {
      const result = await uploadApi.uploadFile(file);
      const fileUrl = result.fileUrl || result.url;
      // Use base URL (without /api) for uploads
      const baseUrl = API_URL.replace('/api', '');
      const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${baseUrl}${fileUrl}`;

      setFormData(prev => ({
        ...prev,
        document: fullUrl,
        documentName: file.name
      }));
    } catch (err) {
      console.error('Error uploading document:', err);
      setError(err.response?.data?.error || 'Failed to upload file. Please try again.');
    } finally {
      setUploadingDocument(false);
      if (documentInputRef.current) {
        documentInputRef.current.value = '';
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    // Check product limit (only for new products, not edits)
    if (!editProductId && productCount >= productLimit) {
      setError(`You have reached your product limit (${productLimit} products) for your ${planDisplayName} plan. Please upgrade to add more products.`);
      return;
    }
    
    // Check image limit - only count images array
    const totalImages = formData.images?.length || 0;
    if (totalImages > maxImages) {
      setError(`Your ${planDisplayName} plan allows maximum ${maxImages} images per product. You have ${totalImages} images.`);
      return;
    }
    
    // Validation - name and category are required
    if (!formData.name && !formData.tagline) {
      setError('Please enter a product name');
      return;
    }
    if (!formData.categoryId) {
      setError('Please select a category');
      return;
    }

    try {
      setSaving(true);
      
      // Set main image from first image in array if not set
      const mainImage = formData.image || (formData.images?.length > 0 ? formData.images[0] : 'https://api.builder.io/api/v1/image/assets/TEMP/da821e6a6c344d588c41324f62b0dda825ecff92?width=800');
      
      const productData = {
        name: formData.name || formData.tagline || 'Product',
        description: formData.description,
        priceMin: parseFloat(formData.priceMin) || 0,
        priceMax: parseFloat(formData.priceMax) || parseFloat(formData.priceMin) || 0,
        unit: formData.unit,
        moq: formData.moq || '1 Unit',
        categoryId: parseInt(formData.categoryId),
        subcategoryId: formData.subcategoryId ? parseInt(formData.subcategoryId) : null,
        sellerId: formData.sellerId ? parseInt(formData.sellerId) : null,
        userId: user?.id || null,
        image: mainImage,
        images: formData.images,
        status: 'active', // Products go live immediately upon publish
        isNew: formData.isNew,
        isFeatured: formData.isFeatured,
        isPopular: formData.isPopular
      };

      if (editProductId) {
        await productsApi.update(editProductId, productData);
        setSuccess('Product updated and published successfully!');
      } else {
        await productsApi.create(productData);
        setSuccess('Product created and published successfully!');
      }

      setPublishSuccess(true);
      
      // Redirect after short delay - back to admin if came from admin panel
      setTimeout(() => {
        if (isAdminEdit) {
          navigate('/admin/products');
        } else {
          navigate('/product-manage');
        }
      }, 2000);

    } catch (err) {
      console.error('Error saving product:', err);
      setError(err.response?.data?.error || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  // Handle Next Step
  const handleNextStep = () => {
    setError(null);
    setCategoryError(false);
    
    // Validation for step 1
    if (currentStep === 1) {
      if (!formData.categoryId) {
        setError('Please select a category');
        setCategoryError(true);
        return;
      }
      // At least product name or tagline should be provided
      if (!formData.name && !formData.tagline) {
        setError('Please enter a product name or tagline');
        return;
      }
    }
    
    // Validation for step 2 - check description has content
    if (currentStep === 2) {
      // Description is optional but warn if empty
      if (!formData.description || formData.description.trim() === '') {
        // Allow to proceed but log warning
        console.log('[ProductInput] Proceeding to preview without description');
      }
    }
    
    // Step 3 is preview - no validation needed, just move to publish
    
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle Previous Step
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Get selected category name
  const getSelectedCategoryName = () => {
    const category = categories.find(c => c.id === parseInt(formData.categoryId));
    return category ? category.name : 'Select Category';
  };

  // Get selected subcategory name
  const getSelectedSubcategoryName = () => {
    const subcategory = subcategories.find(s => s.id === parseInt(formData.subcategoryId));
    return subcategory ? subcategory.name : 'Select Subcategory';
  };

  // Get selected seller name
  const getSelectedSellerName = () => {
    const seller = sellers.find(s => s.id === parseInt(formData.sellerId));
    return seller ? seller.name : 'Select Company';
  };

  // Rich text editor commands
  const execCommand = (command, value = null) => {
    document.execCommand(command, false, value);
    if (editorRef.current) {
      setFormData(prev => ({
        ...prev,
        description: editorRef.current.innerHTML
      }));
    }
  };

  // Handle editor input
  const handleEditorInput = (e) => {
    // Use ref to get current content instead of event target to avoid cursor issues
    const content = editorRef.current ? editorRef.current.innerHTML : e.target.innerHTML;
    setFormData(prev => ({
      ...prev,
      description: content
    }));
  };

  // Handle remove image
  const handleRemoveImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  // Render the main product form content
  const renderProductFormContent = () => (
    <div className={isAdminEdit ? "admin-product-input-content" : "content-wrapper"}>
      {/* Page Header */}
      <div className="page-header" style={isAdminEdit ? { marginBottom: '24px' } : {}}>
        <h1 className="page-title">{editProductId ? 'Edit Product' : 'Add New Product'}</h1>
        {isAdminEdit && (
          <button 
            type="button" 
            className="cancel-btn"
            onClick={() => navigate('/admin/products')}
            style={{ marginLeft: 'auto' }}
          >
            Back to Products
          </button>
        )}
      </div>

      {/* Product Limit Warning - only show when limit reached */}
            {!editProductId && !isAdminEdit && limitInfo.isAtLimit && (
              <div className="product-limit-warning">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <div>
                  <strong>Product Limit Reached!</strong>
                  <p>Your {planDisplayName || 'Basic'} plan allows {productLimit} products. <a href="/membership-packages">Upgrade your plan</a> to add more products.</p>
                </div>
              </div>
            )}

            {/* Progress Steps - Enhanced Design */}
            <div className="enhanced-progress-steps">
              <div className="enhanced-steps-container">
                {/* Step 1 */}
                <div 
                  className={`enhanced-step ${currentStep === 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}
                  onClick={() => currentStep > 1 && setCurrentStep(1)}
                >
                  <div className="enhanced-step-circle">
                    {currentStep > 1 ? (
                      <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                        <path d="M11.5 3.5L5.25 9.75L2.5 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <span>1</span>
                    )}
                  </div>
                  <div className="enhanced-step-label">Basic Info</div>
                </div>

                {/* Connector 1-2 */}
                <div className={`enhanced-connector ${currentStep > 1 ? 'completed' : ''}`}></div>

                {/* Step 2 */}
                <div 
                  className={`enhanced-step ${currentStep === 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}
                  onClick={() => currentStep > 2 && setCurrentStep(2)}
                >
                  <div className="enhanced-step-circle">
                    {currentStep > 2 ? (
                      <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                        <path d="M11.5 3.5L5.25 9.75L2.5 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <span>2</span>
                    )}
                  </div>
                  <div className="enhanced-step-label">Description</div>
                </div>

                {/* Connector 2-3 */}
                <div className={`enhanced-connector ${currentStep > 2 ? 'completed' : ''}`}></div>

                {/* Step 3 */}
                <div 
                  className={`enhanced-step ${currentStep === 3 ? 'active' : ''} ${currentStep > 3 ? 'completed' : ''}`}
                  onClick={() => currentStep > 3 && setCurrentStep(3)}
                >
                  <div className="enhanced-step-circle">
                    {currentStep > 3 ? (
                      <svg width="18" height="18" viewBox="0 0 14 14" fill="none">
                        <path d="M11.5 3.5L5.25 9.75L2.5 7" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <span>3</span>
                    )}
                  </div>
                  <div className="enhanced-step-label">Preview</div>
                </div>

                {/* Connector 3-4 */}
                <div className={`enhanced-connector ${currentStep > 3 ? 'completed' : ''}`}></div>

                {/* Step 4 - Publish */}
                <div className={`enhanced-step publish-step ${currentStep === 4 ? 'active' : ''}`}>
                  <div className="enhanced-step-circle publish">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="enhanced-step-label">Publish</div>
                </div>
              </div>
            </div>

            {/* Form Sections */}
            <form onSubmit={handleSubmit}>
              {/* Error/Success Messages */}
              {error && (
                <div className="alert alert-error">
                  <span>{error}</span>
                  <button type="button" onClick={() => setError(null)}>×</button>
                </div>
              )}
              {success && (
                <div className="alert alert-success">
                  <span>{success}</span>
                </div>
              )}

              {/* Step 1: Basic Information - Category & Seller */}
              {currentStep === 1 && (
              <div className="enhanced-form-section">
                <div className="enhanced-section-header">
                  <div className="section-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 11H5M19 11C20.1046 11 21 11.8954 21 13V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V13C3 11.8954 3.89543 11 5 11M19 11V9C19 7.89543 18.1046 7 17 7M5 11V9C5 7.89543 5.89543 7 7 7M7 7V5C7 3.89543 7.89543 3 9 3H15C16.1046 3 17 3.89543 17 5V7M7 7H17" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="section-header-text">
                    <h2 className="enhanced-section-title">Category Selection</h2>
                    <p className="enhanced-section-desc">Choose the category and subcategory for your product</p>
                  </div>
                </div>
                <div className="enhanced-section-content">
                  <div className="enhanced-form-grid two-cols">
                    <div className="enhanced-form-group">
                      <label className="enhanced-label">Category <span className="required">*</span></label>
                      <div className={`enhanced-select ${categoryError ? 'has-error' : ''}`} onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}>
                        <span className={formData.categoryId ? 'selected' : 'placeholder'}>{getSelectedCategoryName()}</span>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: showCategoryDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }}>
                          <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {showCategoryDropdown && (
                          <div className="enhanced-dropdown">
                            {categories.map(category => (
                              <div 
                                key={category.id} 
                                className="enhanced-dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCategorySelect(category.id);
                                }}
                              >
                                {category.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="enhanced-form-group">
                      <label className="enhanced-label">Sub-category</label>
                      <div className={`enhanced-select ${!formData.categoryId ? 'disabled' : ''}`} onClick={() => formData.categoryId && setShowSubcategoryDropdown(!showSubcategoryDropdown)}>
                        <span className={formData.subcategoryId ? 'selected' : 'placeholder'}>{formData.categoryId ? getSelectedSubcategoryName() : 'Select Category First'}</span>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ transform: showSubcategoryDropdown ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }}>
                          <path d="M6 9L12 15L18 9" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {showSubcategoryDropdown && subcategories.length > 0 && (
                          <div className="enhanced-dropdown">
                            {subcategories.map(subcategory => (
                              <div 
                                key={subcategory.id} 
                                className="enhanced-dropdown-item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSubcategorySelect(subcategory.id);
                                }}
                              >
                                {subcategory.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Step 1 continued: Basic Information - Tagline, Price, Units, MOQ, Description */}
              {currentStep === 1 && (
              <div className="enhanced-form-section">
                <div className="enhanced-section-header">
                  <div className="section-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="section-header-text">
                    <h2 className="enhanced-section-title">Product Details</h2>
                    <p className="enhanced-section-desc">Enter the basic information about your product</p>
                  </div>
                </div>
                <div className="enhanced-section-content">
                  <div className="enhanced-form-group full-width">
                    <label className="enhanced-label">Product Name <span className="required">*</span></label>
                    <input 
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter product name"
                      className="enhanced-input"
                    />
                  </div>
                  <div className="enhanced-form-group full-width">
                    <label className="enhanced-label">Tagline</label>
                    <input 
                      type="text"
                      name="tagline"
                      value={formData.tagline}
                      onChange={handleInputChange}
                      placeholder="A short catchy phrase for your product"
                      className="enhanced-input"
                    />
                  </div>
                  <div className="enhanced-form-grid three-cols">
                    <div className="enhanced-form-group">
                      <label className="enhanced-label">Asking Price</label>
                      <div className="enhanced-input-with-prefix">
                        <span className="input-prefix">$</span>
                        <input 
                          type="number"
                          name="priceMin"
                          value={formData.priceMin}
                          onChange={handleInputChange}
                          placeholder="0.00"
                          step="0.01"
                          className="enhanced-input with-prefix"
                        />
                      </div>
                    </div>
                    <div className="enhanced-form-group">
                      <label className="enhanced-label">Units</label>
                      <select 
                        name="unit"
                        value={formData.unit}
                        onChange={handleInputChange}
                        className="enhanced-select-native"
                      >
                        <option value="Units">Units</option>
                        <option value="Tons">Tons</option>
                        <option value="Kg">Kg</option>
                        <option value="Pieces">Pieces</option>
                        <option value="Bags">Bags</option>
                        <option value="Meters">Meters</option>
                        <option value="Square Meters">Square Meters</option>
                      </select>
                    </div>
                    <div className="enhanced-form-group">
                      <label className="enhanced-label">Minimum Order (MOQ)</label>
                      <input 
                        type="text"
                        name="moq"
                        value={formData.moq}
                        onChange={handleInputChange}
                        placeholder="e.g., 100"
                        className="enhanced-input"
                      />
                    </div>
                  </div>
                  <div className="enhanced-form-group full-width">
                    <label className="enhanced-label">Short Description</label>
                    <textarea 
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder="Briefly describe your product features and benefits..."
                      rows="4"
                      className="enhanced-textarea"
                    />
                  </div>
                </div>
              </div>
              )}

              {/* Step 1: Image & Video */}
              {currentStep === 1 && (
              <div className="enhanced-form-section">
                <div className="enhanced-section-header">
                  <div className="section-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M21 15L16 10L5 21" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="section-header-text">
                    <h2 className="enhanced-section-title">Product Images</h2>
                    <p className="enhanced-section-desc">Upload high-quality images of your product (Max {maxImages} images)</p>
                  </div>
                </div>
                <div className="enhanced-section-content">
                  {/* Image Upload Area */}
                  <div className="enhanced-image-upload-area">
                    {/* Image Preview Grid */}
                    {formData.images.length > 0 && (
                      <div className="enhanced-image-grid">
                        {formData.images.map((img, index) => (
                          <div key={index} className="enhanced-image-item">
                            <img 
                              src={img} 
                              alt={`Product ${index + 1}`}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.style.display = 'none';
                                e.target.parentElement.classList.add('image-broken');
                              }}
                            />
                            {index === 0 && <div className="primary-badge">Primary</div>}
                            <button
                              type="button"
                              className="remove-image-btn"
                              onClick={() => {
                                setFormData(prev => ({
                                  ...prev,
                                  images: prev.images.filter((_, i) => i !== index),
                                  image: index === 0 && prev.images.length > 1 ? prev.images[1] : prev.image
                                }));
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                <path d="M9 3L3 9M3 3L9 9" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Hidden file input for images */}
                    <input
                      type="file"
                      ref={imageInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      style={{ display: 'none' }}
                    />
                    
                    {/* Upload Drop Zone */}
                    {formData.images.length < maxImages && (
                      <div 
                        className="enhanced-upload-dropzone"
                        onClick={() => imageInputRef.current?.click()}
                      >
                        <div className="dropzone-icon">
                          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M17 8L12 3L7 8" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M12 3V15" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                        <div className="dropzone-text">
                          <span className="dropzone-main-text">{uploadingImage ? 'Uploading...' : 'Click to upload images'}</span>
                          <span className="dropzone-sub-text">or drag and drop • PNG, JPG up to 10MB</span>
                        </div>
                        <div className="dropzone-counter">{formData.images.length}/{maxImages} uploaded</div>
                      </div>
                    )}
                    
                    {/* Limit Reached Warning */}
                    {formData.images.length >= maxImages && (
                      <div className="enhanced-limit-warning">
                        <svg width="18" height="18" viewBox="0 0 16 16" fill="none">
                          <path d="M8 1L1 14H15L8 1Z" stroke="#856404" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M8 6V9" stroke="#856404" strokeWidth="1.5" strokeLinecap="round"/>
                          <circle cx="8" cy="11.5" r="0.75" fill="#856404"/>
                        </svg>
                        <span>Maximum {maxImages} images allowed in your plan.</span>
                        {maxImages <= 5 && <a href="/membership">Upgrade for more</a>}
                      </div>
                    )}
                  </div>
                  
                  {/* Document Upload */}
                  <div className="enhanced-document-upload">
                    <label className="enhanced-label">Additional Documents (Optional)</label>
                    <div className="enhanced-document-input">
                      <input
                        type="file"
                        ref={documentInputRef}
                        onChange={handleDocumentUpload}
                        style={{ display: 'none' }}
                      />
                      <button 
                        type="button" 
                        className="enhanced-document-btn"
                        onClick={() => documentInputRef.current?.click()}
                        disabled={uploadingDocument}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M21.44 11.05L12.25 20.24C11.1242 21.3658 9.59718 21.9983 8.00498 21.9983C6.41278 21.9983 4.88582 21.3658 3.76 20.24C2.63419 19.1142 2.00168 17.5872 2.00168 15.995C2.00168 14.4028 2.63419 12.8758 3.76 11.75L12.33 3.18C13.0806 2.42944 14.0991 2.00565 15.1614 2.00565C16.2238 2.00565 17.2423 2.42944 17.9929 3.18C18.7435 3.93056 19.1673 4.94911 19.1673 6.01145C19.1673 7.0738 18.7435 8.09234 17.9929 8.8429L9.41 17.41C9.03472 17.7853 8.52544 17.9971 7.99431 17.9971C7.46318 17.9971 6.9539 17.7853 6.57861 17.41C6.20333 17.0347 5.99144 16.5254 5.99144 15.9943C5.99144 15.4631 6.20333 14.9539 6.57861 14.5786L15.07 6.10001" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span>{uploadingDocument ? 'Uploading...' : (formData.documentName || 'Upload document')}</span>
                      </button>
                      <span className="document-hint">PDF, DOC, DOCX up to 50MB</span>
                    </div>
                  </div>
                </div>
              </div>
              )}

              {/* Step 2: Product Details - Description with Rich Text Editor */}
              {currentStep === 2 && (
              <>
              <div className="enhanced-form-section">
                <div className="enhanced-section-header">
                  <div className="section-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2V8H20" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 13H8" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M16 17H8" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M10 9H9H8" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="section-header-text">
                    <h2 className="enhanced-section-title">Product Description</h2>
                    <p className="enhanced-section-desc">Write a detailed description including features, specifications and benefits</p>
                  </div>
                </div>
                <div className="enhanced-section-content">
                  {/* Rich Text Editor Toolbar */}
                  <div className="enhanced-rich-text-toolbar">
                    <div className="toolbar-group">
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('bold')} title="Bold">
                        <strong>B</strong>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('italic')} title="Italic">
                        <em>I</em>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('underline')} title="Underline">
                        <u>U</u>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('strikeThrough')} title="Strikethrough">
                        <s>S</s>
                      </button>
                    </div>
                    <div className="toolbar-separator"></div>
                    <div className="toolbar-group">
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('justifyLeft')} title="Align Left">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2 3h12v1H2V3zm0 3h8v1H2V6zm0 3h12v1H2V9zm0 3h8v1H2v-1z"/></svg>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('justifyCenter')} title="Align Center">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2 3h12v1H2V3zm2 3h8v1H4V6zm-2 3h12v1H2V9zm2 3h8v1H4v-1z"/></svg>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('justifyRight')} title="Align Right">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2 3h12v1H2V3zm4 3h8v1H6V6zm-4 3h12v1H2V9zm4 3h8v1H6v-1z"/></svg>
                      </button>
                    </div>
                    <div className="toolbar-separator"></div>
                    <div className="toolbar-group">
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('insertUnorderedList')} title="Bullet List">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2 4a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3-1h9v1H5V3zM2 8a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3-1h9v1H5V7zM2 12a1 1 0 1 1 0-2 1 1 0 0 1 0 2zm3-1h9v1H5v-1z"/></svg>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('insertOrderedList')} title="Numbered List">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M2.5 1h1v3h-1V2h-.5v-.5h.5v-.5zM5 3h9v1H5V3zm0 4h9v1H5V7zm0 4h9v1H5v-1zM1.5 6.5v.5h2v.5h-2v.5h2.5V6h-2v-.5h2V5h-2.5v1.5zM1.5 11v1h1v.5H1.5V13h2.5v-.5h-2v-.5h2V11h-2.5z"/></svg>
                      </button>
                    </div>
                    <div className="toolbar-separator"></div>
                    <div className="toolbar-group">
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => {
                        const url = prompt('Enter link URL:');
                        if (url) execCommand('createLink', url);
                      }} title="Insert Link">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M4.715 6.542L3.343 7.914a3 3 0 1 0 4.243 4.243l1.828-1.829A3 3 0 0 0 8.586 5.5L8 6.086a1.001 1.001 0 0 0-.154.199 2 2 0 0 1 .861 3.337L6.88 11.45a2 2 0 1 1-2.83-2.83l.793-.792a4.018 4.018 0 0 1-.128-1.287z"/><path d="M6.586 4.672A3 3 0 0 0 7.414 9.5l.775-.776a2 2 0 0 1-.896-3.346L9.12 3.55a2 2 0 0 1 2.83 2.83l-.793.792c.112.42.155.855.128 1.287l1.372-1.372a3 3 0 0 0-4.243-4.243L6.586 4.672z"/></svg>
                      </button>
                      <button type="button" className="enhanced-toolbar-btn" onClick={() => execCommand('removeFormat')} title="Clear Formatting">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor"><path d="M8.21 1.344L4.5 8.5H2.086a.5.5 0 0 0 0 1h2.414L8.5 3.207l4.086 6.293H15a.5.5 0 0 0 0-1h-2.414L8.21 1.344z"/></svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Rich Text Editor Content Area */}
                  <div 
                    ref={(el) => {
                      editorRef.current = el;
                      // Set initial content only once when element is first created
                      if (el && !el.dataset.initialized) {
                        el.innerHTML = formData.description || '';
                        el.dataset.initialized = 'true';
                      }
                    }}
                    className="enhanced-rich-text-editor"
                    contentEditable
                    onInput={handleEditorInput}
                    suppressContentEditableWarning={true}
                    data-placeholder="Write a detailed description of your product..."
                  />
                </div>
              </div>
              </>
              )}

              {/* Step 3: Preview & Publish */}
              {currentStep >= 3 && (
              <div className="enhanced-form-section enhanced-preview-section">
                <div className="enhanced-section-header">
                  <div className="section-icon preview-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 12C1 12 5 4 12 4C19 4 23 12 23 12C23 12 19 20 12 20C5 20 1 12 1 12Z" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <circle cx="12" cy="12" r="3" stroke="#1F396C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <div className="section-header-text">
                    <h2 className="enhanced-section-title">Preview Your Product</h2>
                    <p className="enhanced-section-desc">Review how your product will appear to buyers before publishing</p>
                  </div>
                </div>
                
                <div className="enhanced-preview-card">
                  {/* Preview Header with Status */}
                  <div className="preview-header">
                    <span className="preview-status">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <circle cx="8" cy="8" r="6" stroke="#1F396C" strokeWidth="2"/>
                      </svg>
                      Draft Preview
                    </span>
                  </div>
                  
                  <div className="enhanced-preview-content">
                    {/* Image Gallery */}
                    <div className="enhanced-preview-gallery">
                      <div className="preview-main-image-container">
                        <img 
                          src={formData.images[0] || formData.image || 'https://via.placeholder.com/400x400?text=Product+Image'} 
                          alt="Product Preview" 
                          className="enhanced-preview-image"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/400x400?text=Product+Image'}
                        />
                      </div>
                      {formData.images.length > 1 && (
                        <div className="preview-thumbnails">
                          {formData.images.slice(0, 4).map((img, index) => (
                            <div key={index} className={`preview-thumb ${index === 0 ? 'active' : ''}`}>
                              <img src={img} alt={`Thumb ${index + 1}`} onError={(e) => e.target.src = 'https://via.placeholder.com/80x80?text=Image'} />
                            </div>
                          ))}
                          {formData.images.length > 4 && (
                            <div className="preview-thumb more">+{formData.images.length - 4}</div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Product Info */}
                    <div className="enhanced-preview-info">
                      <div className="preview-category-badge">
                        {getSelectedCategoryName()}
                        {formData.subcategoryId && <span className="category-separator">›</span>}
                        {formData.subcategoryId && getSelectedSubcategoryName()}
                      </div>
                      
                      <h3 className="enhanced-preview-title">{formData.name || 'Product Name'}</h3>
                      
                      {formData.tagline && (
                        <p className="enhanced-preview-tagline">{formData.tagline}</p>
                      )}
                      
                      <div className="enhanced-preview-price">
                        <span className="price-amount">${formData.priceMin || '0'}</span>
                        <span className="price-unit">/ {formData.unit || 'Unit'}</span>
                      </div>
                      
                      <div className="preview-specs">
                        <div className="spec-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M21 16V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V16C3 17.1046 3.89543 18 5 18H19C20.1046 18 21 17.1046 21 16Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M3 10H21" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span>MOQ: {formData.moq || '1 Unit'}</span>
                        </div>
                        <div className="spec-item">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                            <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <path d="M2 12H22" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                          <span>Seller: {getSelectedSellerName()}</span>
                        </div>
                      </div>
                      
                      <div className="preview-tags">
                        {formData.isNew && <span className="preview-tag new">New</span>}
                        {formData.isFeatured && <span className="preview-tag featured">Featured</span>}
                        {formData.isPopular && <span className="preview-tag popular">Popular</span>}
                      </div>
                      
                      <div className="preview-description-box">
                        <h4>Description</h4>
                        <p>{formData.description || 'No description provided.'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {publishSuccess && (
                  <div className="enhanced-success-message">
                    <div className="success-icon">
                      <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="24" cy="24" r="24" fill="#10B981"/>
                        <path d="M34 16L20 30L14 24" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <h3>Product Published Successfully!</h3>
                    <p>Your product is now live and visible to buyers.</p>
                  </div>
                )}
              </div>
              )}

              {/* Navigation Buttons */}
              <div className="enhanced-form-actions">
                {currentStep > 1 && (
                  <button 
                    type="button" 
                    className="enhanced-btn secondary"
                    onClick={handlePreviousStep}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Previous
                  </button>
                )}
                {currentStep === 1 && (
                  <button 
                    type="button" 
                    className="enhanced-btn secondary"
                    onClick={() => navigate('/product-manage')}
                  >
                    Cancel
                  </button>
                )}
                {currentStep < 4 ? (
                  <button 
                    type="button" 
                    className="enhanced-btn primary"
                    onClick={handleNextStep}
                  >
                    Next Step
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                ) : (
                  <button 
                    type="submit" 
                    className="enhanced-btn publish"
                    disabled={saving || publishSuccess}
                  >
                    {saving ? (
                      <>
                        <svg className="spinner" width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3"/>
                          <path d="M12 2C6.47715 2 2 6.47715 2 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        Publishing...
                      </>
                    ) : publishSuccess ? (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Published!
                      </>
                    ) : (
                      <>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                          <path d="M12 19V5M12 5L5 12M12 5L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        {editProductId ? 'Update & Publish' : 'Publish Product'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
    </div>
  );

  // If admin is editing, use AdminLayout
  if (isAdminEdit) {
    return (
      <AdminLayout>
        <div className="admin-product-edit-page">
          {renderProductFormContent()}
        </div>
      </AdminLayout>
    );
  }

  // Supplier layout - use SellerDashboardLayout for consistency
  return (
    <SellerDashboardLayout>
      <div className="product-input-page product-input-page-content">
        {renderProductFormContent()}
      </div>
    </SellerDashboardLayout>
  );
};

export default ProductInputPage;
